import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router';

import { ProductCard } from '@/features/storefront/components/ProductCard';
import { RAIL_ITEM, Rail } from '@/features/storefront/components/Rail';
import { useAddToCart } from '@/features/storefront/hooks/useCart';
import { useShopContent } from '@/features/storefront/content/useContent';

import { useStorefrontAccountData, useStorefrontScope } from '@/features/storefront/data/context';
import { useStorefrontFeatures } from '@/features/storefront/features';
import { useStorefrontPaths } from '@/features/storefront/paths';

import type { AccountRecommendation } from '../api/account';
import { useCustomerAuth } from '../hooks/useCustomerAuth';
import { useStorefrontTheme } from '@/features/storefront/useStorefrontTheme';

/** Two screens' worth of pushing, the same as the rails under a product. */
const RAIL_SIZE = 12;

/** A card's slot once the row has divided itself up. Matches the storefront rails. */
const RAIL_IMAGE_SIZES = '(min-width: 1024px) 240px, 45vw';


/**
 * What the shop suggests to this shopper, drawn from what they have bought.
 *
 * On the account page it sits below the address rather than above the orders:
 * somebody who opened their account came for an order or an address, and a rail
 * of things to buy is what they read on the way out, not what they came for.
 * The cart repeats it under the basket, where the same is true of a shopper who
 * has finished choosing.
 *
 * Fetched apart from the rest of the page so nothing waits on it, and rendered
 * only once it arrives - a suggestion is allowed to be missing.
 */
export function RecommendedForYou({
  className,
  headingLevel: Heading = 'h3',
}: {
  className?: string;
  /**
   * `h3` inside a page that already heads its own sections, which is how the
   * account page carries it; `h2` where the rail is a section of the page in
   * its own right, as it is under the cart.
   */
  headingLevel?: 'h2' | 'h3';
} = {}) {
  const { status } = useCustomerAuth();
  const copy = useShopContent();
  const account = useStorefrontAccountData();
  const scope = useStorefrontScope();
  const products = useQuery({
    queryKey: [...scope, 'account', 'recommendations', RAIL_SIZE],
    queryFn: () => account.recommendations(RAIL_SIZE),
    // The suggestions are read out of this shopper's own history, so there is
    // nothing to ask for until there is a shopper. The account pages are behind
    // a sign-in already; the cart is not, and a visitor who never signed in
    // would otherwise be turned away by the endpoint on every view of it.
    enabled: status === 'authenticated',
  });

  const data = products.data ?? [];
  if (data.length === 0) return null;
  const heading = copy('account.recommended');

  return (
    <section aria-labelledby="recommended-heading" className={className}>
      <Rail
        label={heading}
        itemCount={data.length}
        heading={
          <Heading id="recommended-heading" className="text-lg font-semibold">
            {heading}
          </Heading>
        }
      >
        {data.map((product) => (
          <li key={product.id} className={RAIL_ITEM}>
            <RecommendationCard product={product} />
          </li>
        ))}
      </Rail>
    </section>
  );
}

/**
 * One suggestion: the catalog's own tile, with the button underneath it.
 *
 * The button sits outside the link rather than inside it, because a button
 * nested in a link is a control a keyboard cannot reach cleanly and a tap
 * target that does two things at once.
 */
function RecommendationCard({ product }: { product: AccountRecommendation }) {
  const theme = useStorefrontTheme();
  const copy = useShopContent();
  const add = useAddToCart();

  const href = useStorefrontPaths().product(product.slug);
  // Null as soon as the product is sold in more than one form: picking the
  // shopper's size for them is not a convenience. Null on a shop with no cart
  // too, which takes the same "view it instead" branch that already exists.
  const { cart: sellsThings } = useStorefrontFeatures();
  const quickAdd = sellsThings ? product.variant_id : null;

  return (
    <div className="flex h-full flex-col">
      <Link to={href} className="group block">
        <ProductCard product={product} sizes={RAIL_IMAGE_SIZES} />
      </Link>

      {/* Pinned to the bottom of the card: a title that wraps to two lines
          would otherwise push this card's button below its neighbours'. */}
      <div className="mt-auto pt-3">
        {!product.in_stock ? null : quickAdd ? (
          <button
            type="button"
            disabled={add.isPending}
            onClick={() => add.mutate({ variantId: quickAdd })}
            className="h-9 w-full px-3 text-xs font-semibold text-white transition-opacity hover:opacity-90 disabled:opacity-60"
            style={{
              backgroundColor: theme?.colors.primary,
              borderRadius: 'var(--storefront-radius)',
            }}
          >
            {add.isPending
              ? copy('product.adding')
              : add.isSuccess
                ? copy('account.added_to_cart')
                : copy('account.add_to_cart')}
          </button>
        ) : (
          <Link
            to={href}
            className="flex h-9 w-full items-center justify-center px-3 text-xs font-semibold text-white transition-opacity hover:opacity-90"
            style={{
              backgroundColor: theme?.colors.primary,
              borderRadius: 'var(--storefront-radius)',
            }}
          >
            {copy('account.choose_options')}
          </Link>
        )}
  
        {add.isError ? (
          <p role="alert" className="mt-1.5 text-xs font-medium text-red-600">
            {copy('account.add_error')}
          </p>
        ) : null}
      </div>
    </div>
  );
}
